import clsx from 'clsx';
import { Icon } from 'common/icon';
import { useRouter } from 'next/router';
import { useCallback, useRef, useState } from 'react';

import styles from './styles.module.css';

import { InputField } from 'common';
import { Button } from 'common/new-button';
import { useAlert } from 'components';
import { Formik, FormikValues } from 'formik';
import { useQuestionsPagination } from 'hooks/useQuestionsPagination';
import { useCreateQuestionMutation } from 'hooks/api/questions';
import { useMeQuery } from 'hooks/api/users';
import { handleMutation } from 'utils/handleMutation';
import { initialValues, model, validationSchema } from './config';

import { Dropdown, DropdownRef } from 'components/dropdown/slots';
import { DropdownItem } from 'components/dropdown/slots/item';
import { SelectArrow } from 'components/modal-arrow';
import { Routes } from 'constants/routes';
import { ICategoryItem } from 'constants/types';
import { useClickOutside } from 'hooks/useClickOutside';

type Props = {
  isVisible: boolean;
  setVisible: (isVisible: boolean) => void;
  onSuccess?: () => void;
};

const categories: ICategoryItem[] = [
  { id: 1, name: 'Other' },
  { id: 2, name: 'News' },
  { id: 3, name: 'Casino complaint' },
  { id: 4, name: 'Topics' },
];

export const CategoryButton = ({
  isOpen,
  onClick,
  placeholder,
  selectedCategory,
}: {
  isOpen?: boolean;
  placeholder?: string;
  onClick?: () => void;
  selectedCategory: ICategoryItem | null;
}) => {
  const handleButtonClick = () => {
    if (onClick) {
      onClick();
    }
  };

  if (!selectedCategory) {
    return (
      <div className={styles.category_row_button} onClick={handleButtonClick}>
        <div className={styles.category_icon}>
          <Icon.MessageThird width="18" height="18" fill="#666" />
        </div>
        <span className={styles.category_name_not_selected}>
          {placeholder ?? 'Choose a category'}
        </span>
        <SelectArrow fill="#121212" type="solid" isOpen={isOpen} />
      </div>
    );
  }

  return (
    <div className={styles.category_row_button} onClick={handleButtonClick}>
      <div className={styles.category_icon}>
        <Icon.MessageThird width="18" height="18" fill="#121212" />
      </div>
      <span className={styles.category_name}>{selectedCategory?.name}</span>
      <SelectArrow fill="#121212" type="solid" isOpen={isOpen} />
    </div>
  );
};

export const TopicModal = ({ onSuccess, isVisible, setVisible }: Props) => {
  const alert = useAlert();
  const router = useRouter();
  const dropdownRef = useRef<DropdownRef | null>(null);
  const modalRef = useRef<HTMLDivElement | null>(null);
  const [isOpen, setIsOpen] = useState(false);
  const { data: me } = useMeQuery();

  const { resetPagination } = useQuestionsPagination({ collection: null });
  const [placeQuestion, mutation] = useCreateQuestionMutation();
  const isLoading = mutation.isLoading;

  const onReviewAdded = useCallback(() => {
    if (onSuccess) {
      onSuccess();
    }
  }, [onSuccess]);

  const handleToggle = () => {
    setIsOpen(!isOpen);
  };

  handleMutation({ ...mutation, onSuccess: onReviewAdded });

  const onSubmit = async (values: FormikValues) => {
    const { category, message, screenshot } = values;

    if (!category && !message) {
      alert.error('Please fill fields');
      return;
    }

    if (!me?.user?._id) {
      router.push(Routes.login);
      return;
    }

    try {
      const response = await placeQuestion({
        message: message,
        parent: null,
        category: category ? JSON.parse(category).name : 'Other',
        image: screenshot,
        title: '',
      });
      const questionId = response?._id;

      if (questionId) {
        router.push(Routes.question(questionId));
      }
      alert.success('Question successfully added');
      resetPagination();
      setVisible(false);
    } catch (error) {
      alert.error('Failed to submit complaint');
    }
  };

  const handleCloseModal = () => {
    setVisible(false);
  };

  useClickOutside({ onClick: handleCloseModal, ref: modalRef });

  if (!isVisible) {
    return null;
  }

  return (
    <div className={clsx(styles.modalBody, styles.modalBodyActive)}>
      <div className={styles.container}>
        <Formik
          onSubmit={onSubmit}
          initialValues={initialValues(initialValues)}
          validationSchema={validationSchema}
        >
          {({ dirty, isSubmitting, setFieldValue, submitForm, values }) => (
            <div className={styles.wrapper} ref={modalRef}>
              <div className={styles.top}>
                <div className={styles.topLeft}>
                  <Icon.MessageThird fill="white" width="20" height="20" />
                  <span>New question</span>
                </div>
                <div
                  className={styles.closeButton}
                  onClick={() => setVisible(false)}
                >
                  <Icon.Close />
                </div>
              </div>
              <Dropdown
                variant={undefined}
                ref={dropdownRef}
                button={
                  <CategoryButton
                    isOpen={isOpen}
                    onClick={handleToggle}
                    selectedCategory={
                      values?.category
                        ? (JSON.parse(values?.category) as ICategoryItem)
                        : null
                    }
                    placeholder={'Choose a category'}
                  />
                }
              >
                {categories?.map((category: ICategoryItem) => (
                  <DropdownItem
                    key={category.id}
                    onClick={() => {
                      setFieldValue('category', JSON.stringify(category));
                      dropdownRef?.current?.close();
                      handleToggle();
                    }}
                  >
                    <div
                      className={clsx(
                        styles.dropdown_item,
                        values?.category &&
                          JSON.parse(values?.category).id === category.id &&
                          styles.dropdown_item_active
                      )}
                    >
                      <div className={styles.category_name}>
                        {category.name}
                      </div>
                    </div>
                  </DropdownItem>
                ))}
              </Dropdown>

              <div className={styles.multiTextInput}>
                <InputField {...model.message} />
              </div>
              <div className={styles.screenshotInput}>
                <InputField {...model.screenshot} />
                <div className={styles.wrapperScreenshotButton}>
                  <Icon.Image />
                  <span>
                    {(values?.screenshot as any)?.name ??
                      'Attach image (optional)'}
                  </span>
                </div>
              </div>
              <div className={styles.submitButton}>
                <Button
                  text={isLoading ? 'Loading...' : 'Create new question'}
                  onClick={submitForm}
                  disabled={!dirty || isSubmitting}
                />
              </div>
            </div>
          )}
        </Formik>
      </div>
    </div>
  );
};
